import React, { useState } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Grid,
  Stack,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import { 
  Refresh, 
  Search, 
  FilterList, 
  KeyboardArrowDown, 
  KeyboardArrowUp, 
  ShoppingCart,
  Person,
  LocalShipping,
  Discount,
  Receipt,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListGuestOrdersQuery,
} from '../../features/admin/guestUserOrderApi.ts';

interface GuestOrderItem {
  id: number;
  guest_order_id: number;
  product_id: number;
  product_quantity: number;
  unit_price: string;
  discount_type: number;
  discount: string;
  tax1_name: string | null;
  tax1_per: string | null;
  tax2_name: string | null;
  tax2_per: string | null;
  price_pack_id: number | null;
  color_pack_id: number | null;
  itemTotal: string;
  discountAmt: string;
  taxAmount: string;
  shippingTotal: string;
  createddt: string;
  updateddt: string | null;
}

interface GuestOrder {
  id: number;
  order_group_id?: number | null;
  order_no?: string; // checkout number the customer sees (#G1234)
  guest_user_id: number;
  guest_user_name: string;
  order_date: string;
  total_amount: string;
  total_order_amount: string;
  total_shipping_charge: string;
  total_discount_amount: string;
  total_tax_amount: string;
  status: number;
  status_text: string;
  delivery_address_id: number;
  delivery_model_id: number | null;
  items?: GuestOrderItem[];
}

// Helper to extract orders array from any response shape
const extractOrders = (data: any): GuestOrder[] => {
  if (!data) return [];
  // Try common response shapes
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.orders)) return data.orders;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  if (data.data && Array.isArray(data.data.orders)) return data.data.orders;
  console.warn('Could not extract orders from response shape:', Object.keys(data));
  return [];
};

const GuestUserOrder = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListGuestOrdersQuery({});

  // Defensively extract orders from whatever shape the API returns
  const orders = extractOrders(data);
  
  const ordersPerPage = 10;
  const totalRecords = orders.length;
  const totalPages = Math.ceil(totalRecords / ordersPerPage);

  // Filter orders based on search
  const filteredOrders = orders.filter((order: GuestOrder) => 
    order.guest_user_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    order.id?.toString().includes(searchStr) ||
    // checkout number the customer quotes: "G1234" / "#G1234"
    (!!order.order_no && `#${order.order_no}`.toLowerCase().includes(searchStr.toLowerCase())) ||
    order.guest_user_id?.toString().includes(searchStr)
  );

  // Paginate orders
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * ordersPerPage,
    page * ordersPerPage
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Orders refreshed successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error refreshing orders',
        severity: 'error'
      });
    }
  };

  const handleToggleExpand = (orderId: number) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount.toString()).toFixed(2)}`;
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return { bgcolor: '#fff3e0', color: '#e65100' };
      case 1: return { bgcolor: '#d1f4e0', color: '#00783e' };
      case 2: return { bgcolor: '#e3f2fd', color: '#1565c0' };
      case 3: return { bgcolor: '#e8eaf6', color: '#303f9f' };
      case 4: return { bgcolor: '#d1f4e0', color: '#00783e' };
      case 5: return { bgcolor: '#ffebee', color: '#c62828' };
      default: return { bgcolor: '#e4e5e7', color: '#616161' };
    }
  };

  const clearFilters = () => {
    setSearchStr('');
    setPage(1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Guest Orders
                </Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#c9cccf',
                    color: '#202223',
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#202223', bgcolor: 'transparent' }
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
                      '& .Mui-selected': { color: '#202223', fontWeight: 600 },
                      '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 }
                    }}
                  >
                    <Tab label={`All (${totalRecords})`} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search orders..."
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 400,
                      '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': { borderColor: '#202223', bgcolor: 'transparent' }
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {/* Show error with details to help debug */}
                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load orders. Please try again.
                      {process.env.NODE_ENV === 'development' && (
                        <Box sx={{ mt: 1, fontSize: '11px', fontFamily: 'monospace' }}>
                          {JSON.stringify(error, null, 2)}
                        </Box>
                      )}
                    </Alert>
                  </Box>
                )}

                {!isLoading && !isError && paginatedOrders.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No guest orders found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr ? `No results for "${searchStr}"` : 'Guest orders will appear here when customers place orders'}
                    </Typography>
                    {searchStr && (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      >
                        Clear search
                      </Button>
                    )}
                  </Box>
                )}

                {!isLoading && paginatedOrders.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header - Desktop */}
                    {!isMobile && (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 100px 1fr 180px 120px 100px 100px 100px 120px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        {['', 'Order ID', 'Customer', 'Order Date', 'Total', 'Shipping', 'Discount', 'Tax', 'Status', 'Address'].map((h) => (
                          <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            {h}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Orders List */}
                    {paginatedOrders.map((order: GuestOrder) => (
                      <Box key={order.id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid',
                            gridTemplateColumns: '50px 100px 1fr 180px 120px 100px 100px 100px 120px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            cursor: 'pointer'
                          } : {
                            p: 2,
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            cursor: 'pointer'
                          }}
                          onClick={() => handleToggleExpand(order.id)}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleExpand(order.id); }} sx={{ color: '#6d7175' }}>
                                {expandedOrderId === order.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                #{order.id}
                                {order.order_no && order.order_no !== String(order.id) && (
                                  <Box component="span" sx={{ ml: 1, color: '#F76338' }}>#{order.order_no}</Box>
                                )}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f2f4', color: '#202223', fontSize: '14px', fontWeight: 600 }}>
                                  {(order.guest_user_name || 'G')[0].toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                    {order.guest_user_name || 'Guest User'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {order.guest_user_id}
                                  </Typography>
                                </Box>
                              </Box>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDate(order.order_date)}
                              </Typography>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>
                                {formatCurrency(order.total_order_amount)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                {formatCurrency(order.total_shipping_charge)}
                              </Typography>

                              <Typography variant="body2" sx={{ color: '#e65100' }}>
                                {formatCurrency(order.total_discount_amount)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                {formatCurrency(order.total_tax_amount)}
                              </Typography>

                              <Chip 
                                label={order.status_text} 
                                size="small"
                                sx={{ ...getStatusColor(order.status), fontWeight: 600, fontSize: '11px' }}
                              />

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                #{order.delivery_address_id}
                              </Typography>
                            </>
                          ) : (
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f2f4', color: '#202223', fontWeight: 600 }}>
                                    {(order.guest_user_name || 'G')[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                      Order #{order.id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {order.guest_user_name}
                                    </Typography>
                                  </Box>
                                </Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleExpand(order.id); }} sx={{ color: '#6d7175' }}>
                                  {expandedOrderId === order.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Chip label={order.status_text} size="small" sx={{ ...getStatusColor(order.status), fontWeight: 600 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>
                                  {formatCurrency(order.total_order_amount)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(order.order_date)}
                              </Typography>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Order Details */}
                        <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Order Items
                            </Typography>
                            
                            {order.items && order.items.length > 0 ? (
                              <Stack spacing={2}>
                                {order.items.map((item: GuestOrderItem) => (
                                  <Card key={item.id} sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                          Product ID: {item.product_id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Item ID: {item.id}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2">Qty: {item.product_quantity}</Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(item.itemTotal)}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                          <Chip icon={<Receipt />} label={`Unit: ${formatCurrency(item.unit_price)}`} size="small" variant="outlined" />
                                          <Chip icon={<Discount />} label={`Discount: ${formatCurrency(item.discountAmt)}`} size="small" variant="outlined" sx={{ color: '#e65100' }} />
                                          {item.tax1_name && <Chip label={`${item.tax1_name}: ${item.tax1_per}%`} size="small" variant="outlined" />}
                                          {item.tax2_name && <Chip label={`${item.tax2_name}: ${item.tax2_per}%`} size="small" variant="outlined" />}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Card>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No items found for this order.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>

              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 },
                      '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuestUserOrder;